import React, { useEffect, useState, useRef, useCallback } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { logout } from '../../slices/authSlice.mjs';
import useIsMounted from '../../hooks/useIsMounted.mjs';
import { setMobileMenuOpen } from '../../slices/headerSlice.mjs';
import { selectNavigationWithForYou } from '../../selectors/navigationSelectors.mjs';
import { setTheme, setLayoutPreference } from '../../slices/pageLayoutSettingsSlice.mjs';
import useCurrentPageType from '../../hooks/useCurrentPageType.mjs';
import { setGuestLayoutPreferences } from '../../utils/layoutPreferencesStorage.mjs';
import ThemePicker from '../common/ThemePicker.mjs';
import AppearanceHub, { DISPLAY_MODES, AVAILABLE_THEMES, LAYOUTS_BY_TYPE } from '../common/AppearanceHub.mjs';
import MobileNavigation from './MobileNavigation.mjs';

export default function Header() {
    const isClient = useIsMounted();
    const [showDropdown, setShowDropdown] = useState(false);
    const [showMoreDropdown, setShowMoreDropdown] = useState(false);
    const [showDesktopTheme, setShowDesktopTheme] = useState(false);
    const [showDesktopLayout, setShowDesktopLayout] = useState(false);
    const dropdownRef = useRef(null);
    const desktopDropdownRef = useRef(null);
    const mobileDropdownRef = useRef(null);
    const moreDropdownRef = useRef(null);

    const auth = useSelector((state) => state.auth || {});
    const isAuthenticated = auth.isAuthenticated || false;
    const user = auth.user || null;

    const mobileMenuOpen = useSelector((state) => state.headerSlice.mobileMenuOpen);

    const resolvedTheme = useSelector(state => state.pageLayoutSettings?.resolvedTheme);
    const currentThemeName = resolvedTheme?.name || 'default';
    const userLayoutPreferences = useSelector(state => state.pageLayoutSettings?.userLayoutPreferences || {});
    const { pageType, label: pageTypeLabel } = useCurrentPageType();
    const layouts = pageType ? LAYOUTS_BY_TYPE[pageType] : [];
    const currentLayout = userLayoutPreferences[pageType] || 'default';

    const { title, description, isArticlePage } = useSelector(
        (state) =>
            state.headerSlice || {
                title: 'Nexus of Truth',
                description: 'Default description.',
            }
    );
    const navigationItems = useSelector(selectNavigationWithForYou);

    const dispatch = useDispatch();

    // Display mode state (mobile header toggle)
    const [displayMode, setDisplayModeLocal] = useState(() => {
        if (typeof window === 'undefined') return 'system';
        const saved = localStorage.getItem('displayMode');
        if (saved && ['light', 'dark', 'system'].includes(saved)) return saved;
        return 'system';
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                desktopDropdownRef.current &&
                !desktopDropdownRef.current.contains(event.target) &&
                mobileDropdownRef.current &&
                !mobileDropdownRef.current.contains(event.target)
            ) {
                setShowDropdown(false);
            }
            if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
                setShowMoreDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Apply display mode to document (for mobile toggle)
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const applyMode = (mode) => {
            const html = document.documentElement;
            if (mode === 'system') {
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                html.classList.toggle('dark', prefersDark);
            } else {
                html.classList.toggle('dark', mode === 'dark');
            }
        };
        applyMode(displayMode);
        if (displayMode === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = () => applyMode('system');
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }
    }, [displayMode]);

    // Cycle display mode: light → dark → system → light
    const cycleDisplayMode = useCallback(async () => {
        const order = ['light', 'dark', 'system'];
        const currentIndex = order.indexOf(displayMode);
        const nextMode = order[(currentIndex + 1) % order.length];
        setDisplayModeLocal(nextMode);
        localStorage.setItem('displayMode', nextMode);

        if (isAuthenticated) {
            try {
                let colorScheme = nextMode;
                if (nextMode === 'system') {
                    colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                }
                await axios.patch('/q/api/user-preferences', { colorScheme, displayMode: nextMode });
            } catch (error) {
                console.warn('Failed to persist display mode preference:', error.message);
            }
        }
    }, [displayMode, isAuthenticated]);

    const toggleDropdown = () => setShowDropdown(!showDropdown);
    const handleLogout = async () => {
        await dispatch(logout());
        setShowDropdown(false);
        dispatch(setMobileMenuOpen(false));
    };

    // Theme change handler for desktop dropdown
    const handleThemeChange = useCallback(async (theme) => {
        dispatch(setTheme({ name: theme.name, version: null }));
        if (typeof document !== 'undefined') {
            const html = document.documentElement;
            if (theme.name === 'default') {
                html.removeAttribute('data-theme');
            } else {
                html.setAttribute('data-theme', theme.name);
            }
        }
        if (isAuthenticated) {
            try {
                await axios.patch('/q/api/user-preferences', {
                    selectedTheme: theme.name === 'default' ? null : theme.name
                });
            } catch (error) {
                console.warn('Failed to persist theme preference:', error.message);
            }
        }
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('selectedTheme', theme.name);
        }
        setShowDesktopTheme(false);
    }, [dispatch, isAuthenticated]);

    // Layout change handler for desktop dropdown
    const handleLayoutChange = useCallback(async (layoutId) => {
        if (!pageType) return;
        dispatch(setLayoutPreference({ pageType, layout: layoutId }));
        if (isAuthenticated) {
            try {
                await axios.patch('/q/api/user-preferences', {
                    layouts: { [pageType]: layoutId }
                });
            } catch (error) {
                console.warn('Failed to persist layout preference:', error.message);
            }
        } else {
            setGuestLayoutPreferences({ [pageType]: layoutId });
        }
        setShowDesktopLayout(false);
    }, [dispatch, isAuthenticated, pageType]);

    // Current display mode info for the cycling toggle
    const currentModeInfo = DISPLAY_MODES.find(m => m.id === displayMode) || DISPLAY_MODES[2];

    const MobileHeaderControls = (
        <div className="lg:hidden flex items-center gap-1">
            {/* Display mode cycling toggle — client-only to avoid hydration mismatch
                (SSR can't read localStorage, so attributes would differ) */}
            {isClient && (
                <button
                    className="display-mode-toggle"
                    onClick={cycleDisplayMode}
                    aria-label={`Display mode: ${currentModeInfo.label}. Tap to switch.`}
                    title={`${currentModeInfo.label} mode`}
                >
                    <span className="display-mode-toggle-icon">{currentModeInfo.icon}</span>
                </button>
            )}
            {/* Hamburger menu */}
            <button
                className="p-2 rounded focus:outline-none"
                aria-label="Open menu"
                onClick={() => dispatch(setMobileMenuOpen(!mobileMenuOpen))}
            >
                <svg
                    className="w-8 h-8 text-theme-text"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
            </button>
        </div>
    );

    const navLink = (item, onLinkClick) =>
        isClient ? (
            <NavLink
                key={item.id}
                to={item.link}
                end={item.link === '/'}
                className={({ isActive }) =>
                    'header-link' + (isActive ? ' active' : '')
                }
                onClick={() => {
                    if (onLinkClick) onLinkClick();
                }}
            >
                {item.label}
            </NavLink>
        ) : (
            <a
                key={item.id}
                href={item.link}
                className="text-lg font-medium text-gray-900 dark:text-white"
                onClick={() => {
                    if (onLinkClick) onLinkClick();
                    // For server-side rendering, we might not dispatch, or handle similarly if needed
                    // But dispatch is client-side only, so this is fine as is
                }}
            >
                {item.label}
            </a>
        );

    const desktopNavLink = (item) => {
        const hasChildren = item.children && item.children.length > 0;

        /**
         * Recursively render a dropdown child item.
         * Each depth level uses a unique group/{name} class for independent hover control.
         * Sub-menus fly out to the right (left-full).
         * 
         * Static class maps ensure Tailwind's JIT scanner discovers these classes.
         */
        const groupClassMap = {
            1: 'group/d1',
            2: 'group/d2',
            3: 'group/d3',
            4: 'group/d4',
        };
        const hoverShowClassMap = {
            1: 'group-hover/d1:block',
            2: 'group-hover/d2:block',
            3: 'group-hover/d3:block',
            4: 'group-hover/d4:block',
        };
        const renderDropdownItem = (child, depth = 1) => {
            const hasSubChildren = child.children && child.children.length > 0;
            const groupClass = groupClassMap[depth] || 'group/d1';
            const hoverShowClass = hoverShowClassMap[depth] || 'group-hover/d1:block';

            return (
                <div key={child.id} className={`relative ${groupClass}`}>
                    {isClient ? (
                        <Link
                            to={child.link}
                            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            {child.label}
                            {hasSubChildren && (
                                <svg
                                    className="absolute right-2 top-3 w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            )}
                        </Link>
                    ) : (
                        <a
                            href={child.link}
                            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            {child.label}
                            {hasSubChildren && (
                                <svg
                                    className="absolute right-2 top-3 w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            )}
                        </a>
                    )}

                    {/* Recursive sub-dropdown */}
                    {hasSubChildren && (
                        <div className={`absolute left-full top-0 mt-0 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 hidden ${hoverShowClass} border border-gray-200 dark:border-gray-700`}>
                            {child.children.map(subChild => renderDropdownItem(subChild, depth + 1))}
                        </div>
                    )}
                </div>
            );
        };

        return (
            <li
                className={`nav-item ${hasChildren ? 'group relative' : ''}`}
                key={item.id}
                onMouseEnter={() => {
                    if (hasChildren) {
                        // Optional: logic to handle hover delay or state if needed
                    }
                }}
                onMouseLeave={() => {
                    if (hasChildren) {
                        // Optional: logic to handle hover delay or state if needed
                    }
                }}
            >
                {isClient ? (
                    <NavLink
                        to={item.link}
                        end={item.link === '/'}
                        className={({ isActive }) =>
                            'nav-link' + (isActive ? ' active' : '')
                        }
                    >
                        {item.label}
                        {hasChildren && (
                            <svg
                                className="ml-1 w-4 h-4 inline-block"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        )}
                    </NavLink>
                ) : (
                    <a href={item.link} className="nav-link">
                        {item.label}
                        {hasChildren && (
                            <svg
                                className="ml-1 w-4 h-4 inline-block"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        )}
                    </a>
                )}

                {/* Dropdown for children — uses recursive renderDropdownItem */}
                {hasChildren && (
                    <div className="absolute left-0 mt-0 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 hidden group-hover:block border border-gray-200 dark:border-gray-700">
                        {item.children.map((child) => renderDropdownItem(child, 1))}
                    </div>
                )}
            </li>
        );
    };

    return (
        <header className="app-header">
            <div className="header-container flex items-center justify-between">
                {/* Left side: Title and description */}
                {isClient ? (
                    <Link
                        to="/"
                        className={`header-info${isArticlePage ? ' header-info-is-article' : ''}`}
                    >
                        <h1>Nexus of Truth</h1>
                        <p>{description}</p>
                    </Link>
                ) : (
                    <a
                        href="/"
                        className={`header-info${isArticlePage ? ' header-info-is-article' : ''}`}
                    >
                        <h1>Nexus of Truth</h1>
                        <p>{description}</p>
                    </a>
                )}
                {/* Mobile controls: display mode toggle + hamburger */}
                {MobileHeaderControls}
                {/* Desktop nav */}
                <nav className="nav-list hidden lg:flex">
                    {navigationItems.slice(0, 7).map((item) => desktopNavLink(item, undefined))}

                    {/* Only show "More" if we have more than 7 navigation items */}
                    {navigationItems.length > 7 && (
                        <li className="nav-item nav-more-item" ref={moreDropdownRef}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent event bubbling
                                    setShowMoreDropdown((prevState) => !prevState);
                                }}
                                className="nav-more-button"
                            >
                                More
                                <svg
                                    className={`nav-more-icon ${showMoreDropdown ? 'transform rotate-180' : ''}`}
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>

                            {showMoreDropdown && (
                                <div className="nav-more-dropdown">
                                    <ul className="nav-more-list">

                                        {navigationItems.slice(7).map((item) => (
                                            <li key={item.id} className="nav-more-item">
                                                {isClient ? (
                                                    <Link
                                                        to={item.link}
                                                        className="nav-more-link"
                                                        onClick={() => {
                                                            setShowMoreDropdown(false);
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                ) : (
                                                    <a
                                                        href={item.link}
                                                        className="nav-more-link"
                                                        onClick={() => setShowMoreDropdown(false)}
                                                    >
                                                        {item.label}
                                                    </a>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </li>
                    )}
                </nav>
                {/* Desktop auth section */}
                <div className="auth-section">
                    {isAuthenticated ? (
                        <div className="user-menu" ref={desktopDropdownRef}>
                            <button className="user-avatar" onClick={toggleDropdown}>
                                {user?.firstName?.[0]}
                                {user?.lastName?.[0] || 'U'}
                            </button>
                            {showDropdown && (
                                <div className="dropdown-menu">
                                    <div className="user-info">
                                        <span className="user-name">
                                            {user?.firstName} {user?.lastName}
                                        </span>
                                        <span className="user-email">{user?.email}</span>
                                    </div>
                                    <ul>
                                        <li>
                                            {isClient ? (
                                                <Link
                                                    to="/my-reviews"
                                                    onClick={() => setShowDropdown(false)}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.7 }}>
                                                        <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                                                    </svg>
                                                    My Reviews
                                                </Link>
                                            ) : (
                                                <a
                                                    href="/my-reviews"
                                                    onClick={() => setShowDropdown(false)}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.7 }}>
                                                        <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                                                    </svg>
                                                    My Reviews
                                                </a>
                                            )}
                                        </li>
                                        <li>
                                            {isClient ? (
                                                <Link
                                                    to="/account"
                                                    onClick={() => setShowDropdown(false)}
                                                >
                                                    Account Settings
                                                </Link>
                                            ) : (
                                                <a
                                                    href="/account"
                                                    onClick={() => setShowDropdown(false)}
                                                >
                                                    Account Settings
                                                </a>
                                            )}
                                        </li>
                                        {/* Theme & Layout section */}
                                        <li className="divider"></li>
                                        <li className="dropdown-appearance-section">
                                            <button
                                                className="dropdown-appearance-toggle"
                                                onClick={() => setShowDesktopTheme(!showDesktopTheme)}
                                            >
                                                <span>🎨</span>
                                                <span>Theme & Layout</span>
                                                <svg className={`dropdown-chevron ${showDesktopTheme ? 'expanded' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                            {showDesktopTheme && (
                                                <div className="dropdown-appearance-content">
                                                    <div className="dropdown-theme-grid">
                                                        {AVAILABLE_THEMES.map(theme => (
                                                            <button
                                                                key={theme.name}
                                                                className={`dropdown-theme-option ${currentThemeName === theme.name ? 'selected' : ''}`}
                                                                onClick={() => handleThemeChange(theme)}
                                                                title={theme.label}
                                                            >
                                                                <span>{theme.icon}</span>
                                                                <span>{theme.label}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                    {pageType && layouts.length > 0 && (
                                                        <>
                                                            <div className="dropdown-layout-label">{pageTypeLabel} Layout</div>
                                                            <div className="dropdown-theme-grid">
                                                                {layouts.map(layout => (
                                                                    <button
                                                                        key={layout.id}
                                                                        className={`dropdown-theme-option ${currentLayout === layout.id ? 'selected' : ''}`}
                                                                        onClick={() => handleLayoutChange(layout.id)}
                                                                        title={layout.description}
                                                                    >
                                                                        <span>{layout.icon}</span>
                                                                        <span>{layout.name}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </li>
                                        <li className="divider"></li>
                                        <li>
                                            <button
                                                className="logout-button"
                                                onClick={handleLogout}
                                            >
                                                Logout
                                            </button>
                                        </li>
                                    </ul>

                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="auth-buttons">
                            {isClient ? (
                                <>
                                    <Link to="/login" className="login-button">
                                        Log In
                                    </Link>
                                    <Link to="/signup" className="signup-button">
                                        Sign Up
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <a href="/login" className="login-button">
                                        Log In
                                    </a>
                                    <a href="/signup" className="signup-button">
                                        Sign Up
                                    </a>
                                </>
                            )}
                            {/* Theme toggle for unauthenticated users (desktop) */}
                        </div>
                    )}
                    {/* Desktop display mode toggle — always visible on lg: for both auth states */}
                    {isClient && (
                        <button
                            className="display-mode-toggle hidden lg:flex"
                            onClick={cycleDisplayMode}
                            aria-label={`Display mode: ${currentModeInfo.label}. Tap to switch.`}
                            title={`${currentModeInfo.label} mode`}
                        >
                            <span className="display-mode-toggle-icon">{currentModeInfo.icon}</span>
                        </button>
                    )}
                    {/* Gear icon only for unauthenticated desktop users (authenticated have avatar dropdown) */}
                    {!isAuthenticated && <AppearanceHub className="hidden lg:block" />}
                </div>
            </div>
            {/* Mobile menu overlay */}
            <MobileNavigation />
        </header>
    );
}
